import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import UserGroupInfo from "./subpage/UserGroupInfo";
import UserGroupMenuSetting from "./subpage/UserGroupMenuSetting";
import {
    deleteUserGroup,
    saveUserGroup,
    selectUserGroups,
    updateUserGroupByUserGroup,
} from "../../../api/usergroup/UserGroupService";
import UserGroupUsers from "./subpage/UserGroupUsers";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {resetPageable, setPageable, setSearch} from "../../../redux/slice/searchPagingSlice";
import {changeRefreshContentStatus} from "../../../redux/slice/statusSlice";
import {setUsers} from "../../../redux/slice/contentSlice";
import {CacheType} from "../../../constant/CodeSpecNum";
import {useCacheRefresh} from "../../../hooks/useCacheRefresh";
import PanelTextNav from "../fieldMng/shared/PanelTextNav";
import UserGroupManageSidebarList from "./subpage/UserGroupManageSidebarList";
import DetailLinkedPagination from "../../../components/detailLayout/DetailLinkedPagination";
import "../../../components/detailLayout/detailLinkedList.css";
import "../userAccount/userAccountManagePanel.css";
import "../company/companyManagePanel.css";

const USER_GROUP_TAB_OPTIONS = [
    {id: "info", label: "기본정보"},
    {id: "users", label: "그룹 사용자 관리"},
    {id: "menus", label: "메뉴 권한"},
];

function UserGroupEditorEmpty({hasSelection}) {
    return (
        <div className="code-editor-empty">
            <div className="code-editor-empty__icon">⌘</div>
            <p className="code-editor-empty__title">
                {hasSelection ? "그룹을 선택하세요" : "등록된 그룹이 없습니다"}
            </p>
            <p className="code-editor-empty__desc">
                {hasSelection
                    ? "왼쪽 목록에서 그룹을 선택하면 이곳에서 상세 정보를 관리할 수 있습니다."
                    : "새 사용자 그룹을 등록해 주세요."}
            </p>
        </div>
    );
}

function UserGroupManagePanel() {
    const [activeTab, setActiveTab] = useState("info");
    const [data, setData] = useState([]);
    const [content, setContent] = useState({});
    const initialFetch = useRef(true);
    const selectedIdRef = useRef(null);
    const {t} = useTranslation();
    const refreshContent = useSelector((state) => state.status.refreshContent);
    const search = useSelector((state) => state.searchPaging.search);
    const pageable = useSelector((state) => state.searchPaging.pageable);
    const dispatch = useDispatch();
    const refreshCache = useCacheRefresh();

    const handleCallUserGroups = async (hasFirstContent) => {
        const response = await selectUserGroups(search, pageable);
        const nextData = [...response.content];
        setData(nextData);
        dispatch(setPageable({
            ...pageable,
            page: response.pageable.pageNumber,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
        }));

        if (hasFirstContent && nextData.length > 0) {
            const first = {...nextData[0]};
            selectedIdRef.current = first.id;
            setContent(first);
            setActiveTab("info");
        } else if (selectedIdRef.current != null) {
            const found = nextData.find((item) => item.id === selectedIdRef.current);
            if (found) {
                setContent({...found});
            } else if (nextData.length > 0) {
                selectedIdRef.current = nextData[0].id;
                setContent({...nextData[0]});
                setActiveTab("info");
            } else {
                selectedIdRef.current = null;
                setContent({});
            }
        }
        return nextData;
    };

    useEffect(() => {
        void handleCallUserGroups(true);
    }, [pageable.page, search]);

    useEffect(() => {
        if (initialFetch.current) {
            initialFetch.current = false;
            return;
        }
        void handleCallUserGroups(false);
    }, [refreshContent]);

    const handleSelectGroup = (item) => {
        if (item?.id == null || item.id === selectedIdRef.current) {
            return;
        }
        selectedIdRef.current = item.id;
        setContent({...item});
        setActiveTab("info");
        dispatch(setUsers([]));
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            dispatch(setSearch({searchTxt: e.target.value}));
            dispatch(resetPageable());
        }
    };

    const handleAddContent = async (groupName) => {
        const userGroupName = groupName?.trim();
        if (!userGroupName) {
            handleActiveError({message: t("noContent")});
            return;
        }
        await saveUserGroup(
            {name: userGroupName},
            () => {
                dispatch(changeRefreshContentStatus());
                handleActiveToast({message: t("saved")});
                refreshCache(CacheType.userGroup);
            },
            () => {
                handleActiveToast({message: t("duplicatedNameExisted")});
            },
        );
    };

    const handleGridPageChange = useCallback((nextPageable) => {
        dispatch(setPageable({
            ...pageable,
            page: nextPageable.pageNumber,
            size: nextPageable.pageSize ?? pageable.size,
        }));
    }, [dispatch, pageable]);

    const handleUpdateUserGroupInfo = async () => {
        if (!content.name?.trim()) {
            handleActiveToast({
                message: t("unableToSave.message"),
                description: t("unableToSave.description.HAS_NO_VALUE"),
            });
            return;
        }
        if (Object.keys(content).length > 0) {
            await updateUserGroupByUserGroup(
                content.id,
                {name: content.name, description: content.description},
                () => {
                    dispatch(changeRefreshContentStatus());
                    handleActiveToast({message: t("updated")});
                    refreshCache(CacheType.userGroup);
                },
            );
        }
    };

    const handleDeleteContent = async (userGroupId) => {
        await deleteUserGroup(
            userGroupId,
            () => {
                if (selectedIdRef.current === userGroupId) {
                    selectedIdRef.current = null;
                    setContent({});
                }
                dispatch(changeRefreshContentStatus());
                handleActiveToast({message: t("removed")});
                refreshCache(CacheType.userGroup);
            },
            () => {
                handleActiveToast({
                    message: t("unableToRemove.message"),
                    description: t("unableToRemove.description.USERGROUP_IN_USE"),
                });
            },
        );
    };

    const showEditor = content?.id != null;
    const searchValue = useMemo(() => search?.searchTxt ?? "", [search?.searchTxt]);
    const gridPageable = useMemo(() => ({
        pageNumber: pageable?.page ?? 0,
        totalPages: Math.max(1, pageable?.totalPages ?? 1),
        totalElements: pageable?.totalElements ?? 0,
        pageSize: pageable?.size ?? 10,
        size: pageable?.size ?? 10,
    }), [pageable]);

    return (
        <div className="code-manage-panel__body company-manage-panel user-group-manage-panel">
            <div className="company-manage-panel__topbar code-manage-scope">
                <div className="company-manage-search">
                    <div className="company-manage-search__input-slot">
                        <input
                            type="text"
                            className="company-manage-search__input basic-input input-style"
                            placeholder="그룹명 검색"
                            defaultValue={searchValue}
                            onKeyDown={handleSearchKeyDown}
                        />
                    </div>
                </div>
            </div>

            <div className="company-manage-panel__workspace">
                <div className="code-manage-panel__list code-manage-scope company-manage-panel__list user-group-manage-panel__list">
                    <UserGroupManageSidebarList
                        items={data}
                        selectedId={content?.id}
                        onSelect={handleSelectGroup}
                        onCreate={handleAddContent}
                    />

                    <div className="user-account-manage-panel__list-footer js-grid-pagination">
                        <DetailLinkedPagination
                            pageable={gridPageable}
                            onPageChange={handleGridPageChange}
                        />
                    </div>
                </div>

                <div className="code-manage-panel__editor code-manage-scope company-manage-panel__editor">
                    <div className="company-manage-editor__head">
                        <div className="company-manage-editor__title-block">
                            <h3 className="company-manage-editor__title">
                                {content?.name ?? "그룹 상세"}
                            </h3>
                        </div>
                        {showEditor && (
                            <PanelTextNav
                                ariaLabel="사용자그룹 상세 탭"
                                options={USER_GROUP_TAB_OPTIONS}
                                value={activeTab}
                                onChange={setActiveTab}
                            />
                        )}
                    </div>

                    <div
                        className={`company-manage-editor__body${
                            showEditor && activeTab !== "info"
                                ? " company-manage-editor__body--tab-list"
                                : ""
                        }`}
                    >
                        {!showEditor ? (
                            <UserGroupEditorEmpty hasSelection={!!data?.length} />
                        ) : (
                            <>
                                {activeTab === "info" && (
                                    <UserGroupInfo
                                        data={{data: content, setData: setContent}}
                                        update={handleUpdateUserGroupInfo}
                                        remove={() => handleDeleteContent(content.id)}
                                    />
                                )}
                                {activeTab === "users" && <UserGroupUsers userGroup={content} />}
                                {activeTab === "menus" && <UserGroupMenuSetting data={content} />}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserGroupManagePanel;
